import { ApplicationStatus } from '@/apis/family_application/requests.ts'
import { useAppeals } from '@/apis/user_appeals/useAppeals.tsx'
import { LogUserActivity } from '@/apis/users/requests'
import { ENV } from '@/app/common/constants.ts'
import { LOG_TYPES } from '@/app/pages/family_application/constants'
import { MinusIcon, PlusIcon } from '@/common/icons/arrows.tsx'
import { cn } from '@/common/utilities.ts'
import { CommonQueries } from '@/queries/common/queries.ts'
import { UserQueries } from '@/queries/users/queries'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice.ts'
import { RootState } from '@/storage/store.ts'
import AceScholarshipsLogo from '@assets/ACEResized1.png'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  TransitionChild,
} from '@headlessui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, Location, useLocation } from 'react-router-dom'
import { useLayout } from '../LayoutProvider.tsx'
import { getMenuItems, MenuLink } from './sidebar.constants.tsx'

const LINKS_TYPES = {
  help_link: 1,
  wallet_handbook_link: 3
}

export const DesktopSidebar = () => {
  const { t: translate } = useTranslation()
  const location = useLocation()
  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )

  const application = useSelector(selectFamilyApplication)
  const SessionUserQuery = useQuery(
    UserQueries.getAuthenticatedUser(application.applicant_id)
  )
  const user = useSelector(() => SessionUserQuery.data)

  const programId = useSelector(selectProgramID)

  // Get dynamic help center link
  const { data: helpLinkData } = useQuery(
    CommonQueries.FetchHelpLink(programId, LINKS_TYPES.help_link)
  )

  const { data: handbookLink } = useQuery(
    CommonQueries.FetchHelpLink(programId, LINKS_TYPES.wallet_handbook_link)
  )

  if (!programSettings.data) {
    return null
  }

  const applicationStatuses: ApplicationStatus[] =
    application.students == undefined || application.students.length == 0
      ? ['In Progress']
      : application.students.map((student) => student.application_status)

  const getLogoSrc = () => {
    return AceScholarshipsLogo
  }

  return (
    <div className="hidden bg-white lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
      {/* Sidebar component, swap this element with another sidebar if you like */}
      <div className="flex grow flex-col gap-y-8 overflow-y-auto border-r border-gray-200 bg-white px-6 py-4">
        <div className="flex h-16 shrink-0 items-center w-full justify-center">
          <img
            alt="Your Company"
            src={getLogoSrc()}
            className="h-auto"
            width="60%"
          />
        </div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" className="space-y-1">
                {getMenuItems(
                  applicationStatuses,
                  programSettings.data,
                  application,
                  helpLinkData?.link,
                  handbookLink?.link
                ).map((menuLink) => (
                  <li key={menuLink.title} className="flex items-center">
                    {menuLink?.children == undefined ? (
                      <>
                        <span
                          className={cn(
                            location.pathname.split('/')[1] ==
                              menuLink.route.split('/')[1]
                              ? 'text-primary-600'
                              : 'text-gray-400 group-hover:text-primary-600',
                            'shrink-0 pr-2'
                          )}
                        >
                          {menuLink.icon}
                        </span>
                        <Link
                          to={menuLink.route}
                          target={
                            menuLink.route.startsWith('http')
                              ? `_blank`
                              : '_self'
                          }
                          className={cn(
                            location.pathname.split('/')[1] ==
                              menuLink.route.split('/')[1]
                              ? 'bg-gray-50 text-primary-600'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600',
                            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6'
                          )}
                          onClick={() => {
                            if (
                              ['handbook', 'help_center'].includes(
                                menuLink.title
                              )
                            )
                              LogUserActivity({
                                log_type: LOG_TYPES.VIEW_PAGE,
                                suffix_text:
                                  menuLink.title == 'handbook'
                                    ? 'the Handbook'
                                    : 'the Help Center',
                              })
                          }}
                        >
                          {translate(menuLink.title)}
                          {menuLink.title == 'messages' &&
                          user?.unread_messages &&
                          user?.unread_messages > 0 ? (
                            <>
                              <span className="flex justify-center items-center px-2 min-w-6 text-xs font-bold text-red-100 bg-red-500 rounded-full">
                                {user.unread_messages}
                              </span>
                            </>
                          ) : (
                            ''
                          )}
                        </Link>
                      </>
                    ) : (
                      <Disclosure>
                        <div className="w-full">
                          <DisclosureButton className="flex w-full items-center">
                            {({ open }) => (
                              <>
                                <span
                                  className={cn('shrink-0 pr-2', {
                                    'text-gray-400 group-hover:text-primary-600':
                                      !menuLinkActive(location, menuLink),
                                    'text-primary-600': menuLinkActive(
                                      location,
                                      menuLink
                                    ),
                                  })}
                                >
                                  {menuLink.icon}
                                </span>
                                <span
                                  className={cn(
                                    'rounded-md p-2 text-sm font-semibold leading-6',
                                    {
                                      'bg-gray-50 text-primary-600':
                                        menuLinkActive(location, menuLink),
                                      'text-gray-700 hover:bg-gray-50 hover:text-primary-600':
                                        !menuLinkActive(location, menuLink),
                                    }
                                  )}
                                >
                                  {translate(menuLink.title)}
                                </span>
                                <span className="text-sm text-gray-400 grow text-end">
                                  {open ? <MinusIcon /> : <PlusIcon />}
                                </span>
                              </>
                            )}
                          </DisclosureButton>
                          <DisclosurePanel as="ul" className="mt-1 px-2">
                            {menuLink.children?.map((subItem, index) => (
                              <li key={index} className="flex items-center">
                                <div
                                  className={cn('w-1.5 h-1.5 rounded', {
                                    'bg-primary-600': subLinkActive(
                                      location,
                                      subItem
                                    ),
                                  })}
                                ></div>
                                <Link
                                  to={subItem.route}
                                  target={
                                    menuLink.route.startsWith('http')
                                      ? `_blank`
                                      : '_self'
                                  }
                                  className="block rounded-md ml-7 p-2 text-sm leading-6 text-gray-700 hover:bg-gray-50"
                                >
                                  {translate(subItem.title)}
                                </Link>
                              </li>
                            ))}
                          </DisclosurePanel>
                        </div>
                      </Disclosure>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </nav>
        {ENV.VITE_APP_VERSION && (
          <div className="text-gray-500 text-center text-xs ">
            v{ENV.VITE_APP_VERSION}
          </div>
        )}
      </div>
    </div>
  )
}

export const MobileSidebar = () => {
  const { t: translate } = useTranslation()
  const { sidebarOpen, setSidebarOpen } = useLayout()
  const location = useLocation()
  const application = useSelector(selectFamilyApplication)
  const appeals = useAppeals()
  const SessionUserQuery = useQuery(
    UserQueries.getAuthenticatedUser(application.applicant_id)
  )
  const user = useSelector(() => SessionUserQuery.data)

  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const programId = useSelector(selectProgramID)

  // Get dynamic help center link
  const { data: helpLinkData } = useQuery(
    CommonQueries.FetchHelpLink(programId, LINKS_TYPES.help_link)
  )

  const { data: handbookLink } = useQuery(
    CommonQueries.FetchHelpLink(programId, LINKS_TYPES.wallet_handbook_link)
  )

  if (!programSettings.data) {
    return null
  }

  if (!appeals.data) {
    return null
  }

  const applicationStatuses: ApplicationStatus[] =
    application.students == undefined || application.students.length == 0
      ? ['In Progress']
      : application.students.map((student) => student.application_status)

  const getLogoSrc = () => {
    return AceScholarshipsLogo
  }

  return (
    <Dialog
      open={sidebarOpen}
      onClose={setSidebarOpen}
      className="bg-white relative z-50 lg:hidden"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 flex">
        <DialogPanel
          transition
          className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
        >
          <TransitionChild>
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="-m-2.5 p-2.5"
              >
                <span className="sr-only">Close sidebar</span>
                <i className="ki-filled ki-cross"></i>
              </button>
            </div>
          </TransitionChild>
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 py-4">
            <div className="flex h-16 shrink-0 items-center justify-center w-full">
              <img
                alt="Your Company"
                src={getLogoSrc()}
                className="h-auto"
                width="60%"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="space-y-4">
                    {getMenuItems(
                      applicationStatuses,
                      programSettings.data,
                      application,
                      helpLinkData?.link,
                      handbookLink?.link
                    ).map((menuLink) => (
                      <li key={menuLink.title} className="flex items-center">
                        {menuLink.children == undefined ? (
                          <>
                            <span
                              className={cn(
                                location.pathname == menuLink.route
                                  ? 'text-primary-600'
                                  : 'text-gray-400 group-hover:text-primary-600',
                                'shrink-0 pr-2'
                              )}
                            >
                              {menuLink.icon}
                            </span>
                            <Link
                              to={menuLink.route}
                              target={
                                menuLink.route.startsWith('http')
                                  ? `_blank`
                                  : '_self'
                              }
                              className={cn(
                                location.pathname == menuLink.route
                                  ? 'bg-gray-50 text-primary-600'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600',
                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6'
                              )}
                              onClick={() => {
                                setSidebarOpen(false)
                                if (
                                  ['handbook', 'help_center'].includes(
                                    menuLink.title
                                  )
                                )
                                  LogUserActivity({
                                    log_type: LOG_TYPES.VIEW_PAGE,
                                    suffix_text:
                                      menuLink.title == 'handbook'
                                        ? 'the Handbook'
                                        : 'the Help Center',
                                  })
                                setSidebarOpen(false)
                              }}
                            >
                              {translate(menuLink.title)}
                              {menuLink.title == 'messages' &&
                              user?.unread_messages &&
                              user?.unread_messages > 0 ? (
                                <>
                                  <span className="flex justify-center items-center px-2 min-w-6 text-xs font-bold text-red-100 bg-red-500 rounded-full">
                                    {user.unread_messages}
                                  </span>
                                </>
                              ) : (
                                ''
                              )}
                            </Link>
                          </>
                        ) : (
                          <Disclosure>
                            <div className="w-full">
                              <DisclosureButton className="flex w-full items-center">
                                {({ open }) => (
                                  <>
                                    <span
                                      className={cn('shrink-0 pr-2', {
                                        'text-gray-400 group-hover:text-primary-600':
                                          !menuLinkActive(location, menuLink),
                                        'text-primary-600': menuLinkActive(
                                          location,
                                          menuLink
                                        ),
                                      })}
                                    >
                                      {menuLink.icon}
                                    </span>
                                    <span
                                      className={cn(
                                        'rounded-md p-2 text-sm font-semibold leading-6',
                                        {
                                          'bg-gray-50 text-primary-600':
                                            menuLinkActive(location, menuLink),
                                          'text-gray-700 hover:bg-gray-50 hover:text-primary-600':
                                            !menuLinkActive(location, menuLink),
                                        }
                                      )}
                                    >
                                      {translate(menuLink.title)}
                                    </span>
                                    <span className="text-sm text-gray-400 grow text-end">
                                      {open ? <MinusIcon /> : <PlusIcon />}
                                    </span>
                                  </>
                                )}
                              </DisclosureButton>
                              <DisclosurePanel
                                as="ul"
                                className="mt-1 px-2"
                                data-headlessui-state="open"
                                data-open
                              >
                                {menuLink.children?.map((subItem, index) => (
                                  <li key={index} className="flex items-center">
                                    <div
                                      className={cn('w-1.5 h-1.5 rounded', {
                                        'bg-primary-600': subLinkActive(
                                          location,
                                          subItem
                                        ),
                                      })}
                                    ></div>
                                    <Link
                                      to={subItem.route}
                                      target={
                                        menuLink.route.startsWith('http')
                                          ? `_blank`
                                          : '_self'
                                      }
                                      className="block rounded-md py-2 pl-9 pr-2 text-sm leading-6 text-gray-700 hover:bg-gray-50"
                                      onClick={() => setSidebarOpen(false)}
                                    >
                                      {translate(subItem.title)}
                                    </Link>
                                  </li>
                                ))}
                              </DisclosurePanel>
                            </div>
                          </Disclosure>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

const menuLinkActive = (location: Location, menuLink: MenuLink) => {
  return location.pathname.split('/')[1] == menuLink.route.split('/')[1]
}

const subLinkActive = (
  location: Location,
  menuLink: Omit<MenuLink, 'icon'>
) => {
  return location.pathname == menuLink.route
}
